import { apiClient } from "@/services/apiClient";

/**
 * A marca — a commercial identity the tenant invoices under.
 *
 * Presentation only. The CFE keeps the legal entity's razón social and RUT; a
 * brand changes the invoice PDF header (name, logo, colour) and the client
 * email. See the approved spec "Marcas: several commercial brands under one
 * legal entity".
 */
export interface Brand {
  id: string;
  name: string;
  /**
   * Null means the brand SHARES the tenant's invoice logo — not that it has no
   * logo. Same null-means-shared convention as the tenant's own
   * `invoiceLogoImage`, so callers must fall back rather than render an empty
   * slot.
   */
  logoImage: string | null;
  /** 0..1 fractions of the source image's natural dimensions. */
  logoCropX: number;
  logoCropY: number;
  logoCropW: number;
  logoCropH: number;
  logoSizePt: number;
  /** Null = inherit the tenant's brand colour, which falls back to Fiscalo's. */
  brandColor: string | null;
  /**
   * This marca's standing adenda (CFE Zona J). THREE states, and they are not
   * interchangeable: null = inherit the company's, "" = this marca
   * deliberately has none, text = this marca's own. Treat null and "" as
   * different or a marca that cleared its adenda silently gets the company's
   * back.
   */
  adendaText: string | null;
  isDefault: boolean;
  sortOrder: number;
  archivedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBrandPayload {
  name: string;
  brandColor?: string | null;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface UpdateBrandPayload {
  name?: string;
  brandColor?: string | null;
  /** True clears the dedicated logo so the brand reuses the tenant's. */
  useSharedLogo?: boolean;
  /** This marca's own adenda. "" means "no adenda", NOT "inherit". */
  adendaText?: string | null;
  /** True clears the marca's adenda so it inherits the company's. */
  useCompanyAdenda?: boolean;
  logoCropX?: number;
  logoCropY?: number;
  logoCropW?: number;
  logoCropH?: number;
  logoSizePt?: number;
  isDefault?: boolean;
  sortOrder?: number;
}

export async function listBrands(): Promise<Brand[]> {
  const { data } = await apiClient.get<{ data: Brand[] }>("/brands");
  return data.data;
}

export async function createBrand(payload: CreateBrandPayload): Promise<Brand> {
  const { data } = await apiClient.post<Brand>("/brands", payload);
  return data;
}

export async function updateBrand(
  brandId: string,
  payload: UpdateBrandPayload
): Promise<Brand> {
  const { data } = await apiClient.patch<Brand>(`/brands/${brandId}`, payload);
  return data;
}

/**
 * Uploads the brand logo. The server RESETS the crop on upload — a crop only
 * means something against the image it was drawn on — so a crop for the new
 * image must be saved with updateBrand AFTER this resolves, never before.
 */
export async function uploadBrandLogo(
  brandId: string,
  file: File
): Promise<Brand> {
  const form = new FormData();
  form.append("image", file);
  const { data } = await apiClient.post<Brand>(`/brands/${brandId}/logo`, form);
  return data;
}

/** Archives the brand: it leaves every picker but still renders on the
 *  invoices already issued under it. */
export async function deleteBrand(brandId: string): Promise<void> {
  await apiClient.delete(`/brands/${brandId}`);
}
